import { useEffect, useRef } from "react";

/**
 * Exits an in-app full-screen overlay when Escape is pressed. The overlay
 * covers the app header, so Escape is the reliable way out regardless of where
 * the exit control has scrolled. Escape is ignored while focus is in an
 * editable field or an AG Grid inline cell editor, so cancelling a cell edit
 * doesn't also exit full screen.
 */
export const useExitFullScreenOnEscape = (
  active: boolean,
  onExit: () => void,
): void => {
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      const { target } = event;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
          target.closest(".ag-cell-inline-editing") != null);
      if (isEditableTarget) return;
      onExitRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active]);
};
